/**
 * CommandPalette — global ⌘K / Ctrl+K launcher to jump to any page or action.
 * Shows Favorites + Recent first, then all pages grouped by module.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Clock } from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../ui/command';
import { flatPages, slugify, prettify } from './modulesConfig';

const CommandPalette = ({ open, onOpenChange, prefs }) => {
  const navigate = useNavigate();
  const { favorites, recents } = prefs;

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  const favPages = useMemo(
    () => favorites.map((p) => flatPages.find((fp) => fp.path === p)).filter(Boolean),
    [favorites],
  );

  const byModule = useMemo(() => {
    const map = {};
    flatPages.forEach((p) => {
      (map[p.moduleTitle] = map[p.moduleTitle] || []).push(p);
    });
    return map;
  }, []);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages and actions..." data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {favPages.length > 0 && (
          <CommandGroup heading="Favorites">
            {favPages.map((p) => (
              <CommandItem
                key={`cmd-fav-${p.path}`}
                value={`fav ${p.label} ${prettify(p.moduleTitle)}`}
                onSelect={() => go(p.path)}
                data-testid={`command-result-${slugify(p.label)}`}
              >
                <Star className="text-yellow-500" />
                <span>{p.label}</span>
                <span className="ml-auto text-xs text-gray-400">{prettify(p.moduleTitle)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {recents.length > 0 && (
          <CommandGroup heading="Recent">
            {recents.map((p) => (
              <CommandItem
                key={`cmd-rec-${p.path}`}
                value={`recent ${p.label}`}
                onSelect={() => go(p.path)}
              >
                <Clock />
                <span>{p.label}</span>
                {p.moduleTitle && <span className="ml-auto text-xs text-gray-400">{prettify(p.moduleTitle)}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {Object.entries(byModule).map(([title, pages]) => (
          <CommandGroup key={title} heading={prettify(title)}>
            {pages.map((p) => {
              const Icon = p.icon;
              return (
                <CommandItem
                  key={`cmd-${p.moduleId}-${p.path}-${p.label}`}
                  value={`${p.label} ${prettify(title)}`}
                  onSelect={() => go(p.path)}
                  data-testid={`command-result-${slugify(p.label)}`}
                >
                  {Icon ? <Icon /> : null}
                  <span>{p.label}</span>
                  <span className="ml-auto text-xs text-gray-400">{prettify(title)}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
